import { FormSchema } from 'types/form-schema';
import { Precedent } from 'types/precedent';
import { SourceInstitution } from 'types/source-institution';
import { extractSourceInstitutionID } from 'utils/required-credentials';
import { getHeadingAndSubHeading } from 'utils/required-credential';
import { getTotalCreditPoints as getSubjectsCP } from 'utils/subject';

/**
 * Given a search term, returns a list of matching Precedents.
 * @export
 * @param {Precedent[]} precedents
 * @param {SourceInstitution} sourceInstitution
 * @param {FormSchema} formSchema
 * @param {string} searchTerm
 * @return {*}  {Precedent[]}
 */
export function getFilteredPrecedents(
  precedents: Precedent[],
  sourceInstitution: SourceInstitution,
  formSchema: FormSchema,
  searchTerm: string,
): Precedent[] {
  const searchTermTransformed = searchTerm.trim().toLowerCase();

  const filteredPrecdents = precedents.filter((precedent) => {
    const sourceInstitutions = precedent.requiredCredentials.map(extractSourceInstitutionID);
    if (!sourceInstitutions.includes(sourceInstitution.ID)) {
      return false;
    }
    const sourceSubjectMatched = precedent.requiredCredentials.some((credential) => {
      const { headings, subHeadings } = formSchema[credential.type];
      return credential.matchingCriteria.some(
        (match) =>
          [...headings, ...subHeadings].includes(match.field) &&
          match.value.trim().toLowerCase().includes(searchTermTransformed),
      );
    });
    const targetSubjectMatched = precedent.subjects.some(
      (subject) =>
        subject.name.toLowerCase().includes(searchTermTransformed) ||
        subject.code.toLowerCase().includes(searchTermTransformed),
    );
    return sourceSubjectMatched || targetSubjectMatched;
  });
  return filteredPrecdents;
}

/**
 * Sorts Precedents by credentials (asc) > credit points (desc) > subjects (asc).
 * Does not mutate input array, returns a new array.
 * @export
 * @param {Precedent[]} precedents
 * @param {FormSchema} formSchema
 * @return {*}  {Precedent[]}
 */
export function sortInitialPrecedents(precedents: Precedent[], formSchema: FormSchema): Precedent[] {
  return [...precedents].sort((a, b) => {
    // Sort on precedent credential heading names
    const aHeadings = a.requiredCredentials
      .map((credential) => {
        const { heading } = getHeadingAndSubHeading(credential, formSchema);
        return heading;
      })
      .join(', ');
    const bHeadings = b.requiredCredentials
      .map((credential) => {
        const { heading } = getHeadingAndSubHeading(credential, formSchema);
        return heading;
      })
      .join(', ');
    if (aHeadings === bHeadings) {
      const [aCP, bCP] = [getTotalCreditPoints(a), getTotalCreditPoints(b)];
      if (aCP === bCP) {
        const [aSubject, bSubject] = [
          a.subjects.map((subject) => subject.name.toLowerCase()).join(' '),
          b.subjects.map((subject) => subject.name.toLowerCase()).join(' '),
        ];
        return aSubject.localeCompare(bSubject);
      }
      return aCP > bCP ? -1 : 1;
    }
    return aHeadings.localeCompare(bHeadings);
  });
}

/**
 * Calculates the total credits points for a Precedent.
 * @export
 * @param {Precedent} precedent
 * @return {*}  {number}
 */
export function getTotalCreditPoints(precedent: Precedent): number {
  return getSubjectsCP(precedent.subjects);
}
